"use client";
import { useEffect, useState } from "react";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import { useTranslation } from "@/hooks/translationContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import Squares2X2Icon from "@heroicons/react/24/outline/Squares2X2Icon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";

/**
 * Desktop-only compact search bar that pins to the top of the viewport once the
 * hero search card has scrolled above it. The homepage header is not sticky, so
 * this bar replaces it while the user browses the page below the hero.
 */
export default function HeroStickySearch() {
  const { setIsOpen, setCurrentTab, setInitialFocus } = useSearchContext();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const sentinel = document.getElementById("hero-search-sentinel");
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show once the sentinel has scrolled above the top of the viewport.
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const districtLabel = t("header-quicksearch-district", { defaultValue: "Semt Seçin" });
  const serviceLabel = t("header-quicksearch-service", { defaultValue: "Hizmet Seçin" });
  const searchLabel = t("text-search", { defaultValue: "Ara" });

  const openSearch = (focus: "district" | "service" | null) => {
    setCurrentTab(0);
    setInitialFocus(focus);
    setIsOpen(true);
  };

  return (
    <div
      className={cn(
        "hidden md:block fixed inset-x-0 top-0 z-[100] transition-transform duration-300 ease-in-out",
        visible ? "translate-y-0" : "-translate-y-full pointer-events-none",
      )}
    >
      <div className="bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="container flex items-center gap-3 py-2">
          <StickyTrigger icon={MapPinIcon} label={districtLabel} onClick={() => openSearch("district")} />
          <StickyTrigger icon={Squares2X2Icon} label={serviceLabel} onClick={() => openSearch("service")} />
          <Button
            onClick={() => openSearch(null)}
            variant="primary-dark"
            className="!h-11 w-40 rounded-xl font-bold text-sm gap-2 shrink-0"
          >
            <MagnifyingGlassIcon className="size-4" />
            {searchLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StickyTrigger({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 flex items-center justify-between gap-2 px-4 h-11 rounded-xl border border-gray-200 text-left hover:bg-gray-secondary transition-colors"
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon className="size-5 text-primary shrink-0" />
        <span className="text-sm text-primary font-semibold truncate">{label}</span>
      </span>
      <ChevronDownIcon className="size-4 text-primary shrink-0" />
    </button>
  );
}
